'use client';
import React, { useState, useEffect } from 'react';
import PublicTopbar from '@/components/PublicTopbar';
import PublicFooter from '@/app/components/PublicFooter';
import AppImage from '@/components/ui/AppImage';
import { Play, X, ExternalLink, Loader2, Video } from 'lucide-react';

interface VideoItem {
  id: string;
  title: string;
  artist: string;
  year: string;
  type: string;
  thumbnail: string;
  thumbnailAlt: string;
  embedUrl?: string;
  youtubeUrl?: string;
  duration: string;
  views: string;
  featured: boolean;
}

export default function VideosPage() {
  const [activeVideo, setActiveVideo] = useState<VideoItem | null>(null);
  const [videos, setVideos] = useState<VideoItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('/api/videos')
      .then((r) => r.json())
      .then((data) => setVideos(data.videos ?? []))
      .catch(() => setVideos([]))
      .finally(() => setLoading(false));
  }, []);

  const featuredVideo = videos.find((v) => v.featured);
  const otherVideos = videos.filter((v) => !v.featured);

  return (
    <div className="min-h-screen bg-background">
      <PublicTopbar />

      {/* Header */}
      <section className="pt-32 pb-12 bg-background">
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-10">
          <p className="text-[11px] font-600 tracking-widest uppercase text-primary mb-2">Visual</p>
          <div className="flex items-end justify-between">
            <h1 className="text-5xl font-800 text-foreground">Videos</h1>
            <a
              href="https://www.youtube.com/@hadatha"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors">
              <ExternalLink size={16} />
              YouTube Channel
            </a>
          </div>
        </div>
      </section>

      {/* Loading state */}
      {loading && (
        <section className="pb-24 bg-background">
          <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 flex justify-center py-24">
            <Loader2 size={32} className="animate-spin text-primary" />
          </div>
        </section>
      )}

      {!loading && (
        <>
          {/* Featured video */}
          {featuredVideo && (
            <section className="pb-12 bg-background">
              <div className="max-w-screen-2xl mx-auto px-6 lg:px-10">
                <div
                  className="relative rounded-2xl overflow-hidden bg-card border border-border card-glow group cursor-pointer"
                  onClick={() => setActiveVideo(featuredVideo)}>
                  <div className="relative aspect-video">
                    <AppImage
                      src={featuredVideo.thumbnail}
                      alt={featuredVideo.thumbnailAlt}
                      fill
                      className="object-cover group-hover:scale-105 transition-transform duration-700"
                      sizes="100vw"
                    />
                    <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-colors" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-20 h-20 rounded-full bg-primary/90 flex items-center justify-center gold-glow group-hover:scale-110 transition-transform duration-300">
                        <Play size={32} className="text-primary-foreground translate-x-1" fill="currentColor" />
                      </div>
                    </div>
                    <div className="absolute top-4 left-4">
                      <span className="px-3 py-1 text-[10px] font-700 tracking-widest uppercase rounded-full bg-primary/90 text-primary-foreground">
                        Latest · {featuredVideo.year}
                      </span>
                    </div>
                    {featuredVideo.duration && (
                      <div className="absolute bottom-4 right-4 px-2 py-1 rounded bg-black/70 text-xs font-mono text-white">
                        {featuredVideo.duration}
                      </div>
                    )}
                  </div>
                  <div className="p-6">
                    <span className="text-[10px] font-600 tracking-widest uppercase text-primary">{featuredVideo.type}</span>
                    <h2 className="text-2xl font-700 text-foreground mt-1 mb-1">{featuredVideo.title}</h2>
                    <div className="flex items-center gap-3 text-sm text-muted-foreground">
                      <span>{featuredVideo.artist}</span>
                      <span className="text-border">·</span>
                      <span className="font-tabular">{featuredVideo.views} views</span>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          )}

          {/* Video grid */}
          <section className="pb-24 bg-background">
            <div className="max-w-screen-2xl mx-auto px-6 lg:px-10">
              {otherVideos.length === 0 && !featuredVideo ? (
                <div className="text-center py-24">
                  <Video size={48} className="text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">No videos available yet.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {otherVideos.map((video) => (
                    <div
                      key={video.id}
                      className="group cursor-pointer rounded-xl overflow-hidden bg-card border border-border card-glow-hover transition-all duration-200"
                      onClick={() => setActiveVideo(video)}>
                      <div className="relative aspect-video overflow-hidden">
                        <AppImage
                          src={video.thumbnail}
                          alt={video.thumbnailAlt}
                          fill
                          className="object-cover group-hover:scale-110 transition-transform duration-500"
                          sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                        />
                        <div className="absolute inset-0 bg-black/40 group-hover:bg-black/20 transition-colors" />
                        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                          <div className="w-14 h-14 rounded-full bg-primary/90 flex items-center justify-center">
                            <Play size={22} className="text-primary-foreground translate-x-0.5" fill="currentColor" />
                          </div>
                        </div>
                        {video.duration && (
                          <div className="absolute bottom-2 right-2 px-1.5 py-0.5 rounded bg-black/70 text-[10px] font-mono text-white">
                            {video.duration}
                          </div>
                        )}
                      </div>
                      <div className="p-4">
                        <span className="text-[9px] font-600 tracking-widest uppercase text-primary">{video.type} · {video.year}</span>
                        <h3 className="text-sm font-700 text-foreground mt-0.5 truncate">{video.title}</h3>
                        <p className="text-xs text-muted-foreground font-tabular mt-0.5">{video.views} views</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>
        </>
      )}

      {/* Video modal */}
      {activeVideo && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4 animate-fade-in"
          onClick={() => setActiveVideo(null)}>
          <div
            className="relative w-full max-w-4xl bg-card rounded-2xl overflow-hidden border border-border animate-scale-in"
            onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setActiveVideo(null)}
              className="absolute top-4 right-4 z-10 p-2 rounded-full bg-black/60 text-white hover:bg-black/80 transition-all"
              aria-label="Close video">
              <X size={20} />
            </button>
            {activeVideo.embedUrl ? (
              <div className="relative aspect-video">
                <iframe
                  src={`${activeVideo.embedUrl}?autoplay=1`}
                  title={activeVideo.title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full"
                />
              </div>
            ) : (
              <div className="relative aspect-video">
                <AppImage
                  src={activeVideo.thumbnail}
                  alt={activeVideo.thumbnailAlt}
                  fill
                  className="object-cover"
                  sizes="100vw"
                />
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/60 gap-4">
                  <p className="text-white/70 text-sm">Video not available for inline playback</p>
                  {activeVideo.youtubeUrl && (
                    <a
                      href={activeVideo.youtubeUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-gold text-primary-foreground text-sm font-700 hover:opacity-90 transition-all">
                      <ExternalLink size={16} />
                      Watch on YouTube
                    </a>
                  )}
                </div>
              </div>
            )}
            <div className="p-5">
              <span className="text-[10px] font-600 tracking-widest uppercase text-primary">{activeVideo.type} · {activeVideo.year}</span>
              <h3 className="text-lg font-700 text-foreground mt-1">{activeVideo.title}</h3>
              <p className="text-sm text-muted-foreground">{activeVideo.artist}</p>
            </div>
          </div>
        </div>
      )}

      <PublicFooter />
    </div>
  );
}
